import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import {
  Sun, Coffee, Moon, Plus, Trash2, ChevronUp, ChevronDown,
  RefreshCw, Save, X, AlertTriangle, CheckCircle, Edit3, Sparkles
} from 'lucide-react';

const C = {
  yellow:'#F7EC71', orange:'#E4AC49', orangeLight:'#FDF3DC',
  green:'#7EA594', greenLight:'#EAF2EF', cream:'#F7F6EF', charcoal:'#2F3A42',
  navy:'#445366', mid:'#7A8A96', border:'#D8DEE3', skyBlue:'#BCD9E5',
  skyBlueLight:'#EAF4F8', skyText:'#3A8FA3', white:'#FFFFFF',
  red:'#DC3545', redLight:'#FFF0F0', amber:'#E07B00', amberLight:'#FFF3E0',
  pink:'#F2B8CB', pinkLight:'#FDF0F5', pinkActive:'#C4607A',
  sunrise:'#E8923A', sunriseLight:'#FDF3E7',
  purple:'#8B7EC8', purpleLight:'#F0EEF9',
};

const VENUE_DEPTS = {
  'Box Jordaan':  ['bar', 'kitchen'],
  'Box Plantage': ['bar', 'kitchen'],
  'Sassys':       ['kitchen'],
  'Prep Kitchen': ['kitchen'],
};
const ALL_VENUES = Object.keys(VENUE_DEPTS);

// Bar shifts: opening, mid-shift-bar, mid-shift-floor, closing-bar, closing-floor, cleaning
// Kitchen shifts: opening (optional), closing (optional), cleaning
const BAR_SHIFTS     = ['opening', 'mid-shift-bar', 'mid-shift-floor', 'closing-bar', 'closing-floor', 'cleaning'];
const KITCHEN_SHIFTS = ['cleaning'];

const SHIFT_META = {
  'opening':         { label: 'Opening',           icon: Sun,      colors: { active: '#E8923A', bg: '#FDF3E7' } },
  'mid-shift-bar':   { label: 'Mid-Shift · Bar',   icon: Coffee,   colors: { active: '#3A8FA3', bg: '#EAF4F8' } },
  'mid-shift-floor': { label: 'Mid-Shift · Floor', icon: Coffee,   colors: { active: '#3A8FA3', bg: '#EAF4F8' } },
  'closing-bar':     { label: 'Closing · Bar',     icon: Moon,     colors: { active: '#445366', bg: '#EEF1F5' } },
  'closing-floor':   { label: 'Closing · Floor',   icon: Moon,     colors: { active: '#445366', bg: '#EEF1F5' } },
  'cleaning':        { label: 'Cleaning',           icon: Sparkles, colors: { active: '#8B7EC8', bg: '#F0EEF9' } },
};

const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const DEPT_LABELS = { bar: 'Bar', kitchen: 'Kitchen' };

const INTERVAL_OPTIONS = [
  { value: 1,  label: 'Every day'     },
  { value: 2,  label: 'Every 2 days'  },
  { value: 3,  label: 'Every 3 days'  },
  { value: 7,  label: 'Every week'    },
  { value: 10, label: 'Every 10 days' },
  { value: 14, label: 'Every 2 weeks' },
  { value: 21, label: 'Every 3 weeks' },
  { value: 30, label: 'Every month'   },
];

function daysSince(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  const now = new Date(); now.setHours(0,0,0,0);
  return Math.floor((now - d) / 86400000);
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const dayPill = (active) => ({
  padding: '0.22rem 0.45rem', borderRadius: '5px',
  border: `1px solid ${active ? C.navy : C.border}`,
  background: active ? C.navy : C.white,
  color: active ? C.white : C.mid,
  fontSize: '0.72rem', cursor: 'pointer', fontWeight: active ? 600 : 400,
  userSelect: 'none', WebkitTapHighlightColor: 'transparent',
});

function PillRow({ options, value, onChange, color }) {
  return (
    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
      {options.map(o => {
        const active = value === o.value;
        return (
          <button key={o.value} onClick={() => onChange(o.value)} style={{
            padding: '0.3rem 0.8rem', borderRadius: '999px',
            border: `1px solid ${active ? color : C.border}`,
            background: active ? color : C.white,
            color: active ? C.white : C.mid,
            fontSize: '0.8rem', cursor: 'pointer',
            fontWeight: active ? 600 : 400,
          }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Template editor ──────────────────────────────────────────────────────────
function ShiftTemplateEditor({ venue, department, shift, onSaved }) {
  const [items, setItems]       = useState([]);
  const [newLabel, setNewLabel] = useState('');
  const [newDays, setNewDays]   = useState([]);
  const [saving, setSaving]     = useState(false);
  const [loaded, setLoaded]     = useState(false);
  const [dirty, setDirty]       = useState(false);

  useEffect(() => {
    setLoaded(false); setDirty(false);
    supabase.from('checklist_templates')
      .select('items')
      .eq('venue', venue).eq('department', department).eq('shift', shift)
      .maybeSingle()
      .then(({ data }) => { setItems(data?.items || []); setLoaded(true); });
  }, [venue, department, shift]);

  function markDirty() { setDirty(true); }

  function moveItem(id, dir) {
    setItems(prev => {
      const idx = prev.findIndex(i => i.id === id);
      const t   = dir === 'up' ? idx - 1 : idx + 1;
      if (t < 0 || t >= prev.length) return prev;
      const n = [...prev]; [n[idx], n[t]] = [n[t], n[idx]]; return n;
    });
    markDirty();
  }

  function updateLabel(id, val) {
    setItems(prev => prev.map(i => i.id === id ? { ...i, label: val } : i));
    markDirty();
  }

  function toggleDay(id, day) {
    setItems(prev => prev.map(i => {
      if (i.id !== id) return i;
      const days = i.days || [];
      return { ...i, days: days.includes(day) ? days.filter(d => d !== day) : [...days, day] };
    }));
    markDirty();
  }

  function setAllDays(id) {
    setItems(prev => prev.map(i => i.id === id ? { ...i, days: null } : i));
    markDirty();
  }

  function removeItem(id) {
    setItems(prev => prev.filter(i => i.id !== id));
    markDirty();
  }

  function addItem() {
    if (!newLabel.trim()) return;
    const shiftSlug = shift.replace(/-/g, '').slice(0, 8);
    const id = `${venue.toLowerCase().replace(/\s/g,'')}-${department[0]}-${shiftSlug}-${Date.now()}`;
    setItems(prev => [...prev, { id, label: newLabel.trim(), days: newDays.length > 0 ? newDays : null }]);
    setNewLabel(''); setNewDays([]); markDirty();
  }

  async function handleSave() {
    setSaving(true);
    await supabase.from('checklist_templates').upsert(
      { venue, department, shift, items, updated_at: new Date().toISOString() },
      { onConflict: 'venue,department,shift' }
    );
    setSaving(false); setDirty(false);
    if (onSaved) onSaved();
  }

  const meta      = SHIFT_META[shift] || SHIFT_META['opening'];
  const ShiftIcon = meta.icon;
  const sc        = meta.colors;

  if (!loaded) return (
    <div style={{ padding: '2rem', textAlign: 'center', color: C.mid, fontSize: '0.85rem' }}>Loading…</div>
  );

  return (
    <div style={{ background: sc.bg, borderRadius: '12px', padding: '1.25rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <ShiftIcon size={16} style={{ color: sc.active }} />
          <span style={{ fontWeight: 700, fontSize: '0.95rem', color: C.charcoal }}>{meta.label}</span>
          <span style={{ fontSize: '0.72rem', color: C.mid }}>{items.length} item{items.length !== 1 ? 's' : ''}</span>
          {dirty && <span style={{ fontSize: '0.68rem', color: C.amber, fontWeight: 600 }}>● unsaved</span>}
        </div>
        <button onClick={handleSave} disabled={!dirty || saving} style={{
          display: 'flex', alignItems: 'center', gap: '0.35rem',
          padding: '0.35rem 0.9rem', borderRadius: '7px',
          background: dirty ? sc.active : C.border,
          color: C.white, border: 'none',
          fontSize: '0.78rem', fontWeight: 600,
          cursor: dirty ? 'pointer' : 'default',
        }}>
          <Save size={12} />{saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div style={{ background: C.white, borderRadius: '10px', overflow: 'hidden', marginBottom: '0.85rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        {items.length === 0 && (
          <p style={{ padding: '1.1rem', color: C.mid, fontSize: '0.84rem', margin: 0, textAlign: 'center' }}>No items yet — add one below</p>
        )}
        {items.map((item, idx) => (
          <div key={item.id} style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.65rem 0.85rem',
            borderBottom: idx < items.length - 1 ? `1px solid ${C.border}` : 'none',
            flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', flexShrink: 0 }}>
              <button onClick={() => moveItem(item.id, 'up')} disabled={idx === 0}
                style={{ background: 'none', border: 'none', cursor: idx === 0 ? 'default' : 'pointer', color: idx === 0 ? C.border : C.mid, padding: '1px', lineHeight: 1 }}>
                <ChevronUp size={13} />
              </button>
              <button onClick={() => moveItem(item.id, 'down')} disabled={idx === items.length - 1}
                style={{ background: 'none', border: 'none', cursor: idx === items.length - 1 ? 'default' : 'pointer', color: idx === items.length - 1 ? C.border : C.mid, padding: '1px', lineHeight: 1 }}>
                <ChevronDown size={13} />
              </button>
            </div>
            <input value={item.label} onChange={e => updateLabel(item.id, e.target.value)}
              style={{ flex: '1 1 160px', minWidth: '120px', padding: '0.32rem 0.5rem', border: `1px solid ${C.border}`, borderRadius: '6px', fontSize: '0.87rem', color: C.charcoal }} />
            {shift !== 'cleaning' && (
              <div style={{ display: 'flex', gap: '0.22rem', flexWrap: 'wrap', flex: '0 0 auto' }}>
                <button onClick={() => setAllDays(item.id)} style={dayPill(!item.days)}>All</button>
                {DAYS.map(d => (
                  <button key={d} onClick={() => toggleDay(item.id, d)} style={dayPill(item.days?.includes(d))}>{d}</button>
                ))}
              </div>
            )}
            <button onClick={() => removeItem(item.id)}
              style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', flexShrink: 0, padding: '0.2rem' }}>
              <X size={15} />
            </button>
          </div>
        ))}
      </div>

      {/* Add new item */}
      <div style={{ background: C.white, borderRadius: '10px', padding: '0.85rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input value={newLabel} onChange={e => setNewLabel(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addItem()}
            placeholder='New item…'
            style={{ flex: '1 1 160px', minWidth: '120px', padding: '0.38rem 0.55rem', border: `1px solid ${C.border}`, borderRadius: '7px', fontSize: '0.87rem', color: C.charcoal }} />
          {shift !== 'cleaning' && (
            <div style={{ display: 'flex', gap: '0.22rem', flexWrap: 'wrap' }}>
              {DAYS.map(d => (
                <button key={d} onClick={() => setNewDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d])} style={dayPill(newDays.includes(d))}>{d}</button>
              ))}
            </div>
          )}
          <button onClick={addItem} style={{
            padding: '0.38rem 1rem', background: C.navy, color: C.white,
            border: 'none', borderRadius: '7px', fontSize: '0.82rem',
            fontWeight: 600, cursor: 'pointer', flexShrink: 0,
          }}>Add</button>
        </div>
        <p style={{ margin: '0.28rem 0 0', fontSize: '0.67rem', color: C.mid }}>
          {shift === 'cleaning'
            ? 'Cleaning items are tracked by days since last completion — no day schedule needed'
            : 'No days selected = every day · Press Enter to add'
          }
        </p>
      </div>
    </div>
  );
}

// ─── Recurring task row ───────────────────────────────────────────────────────
function RecurringTaskRow({ task, onUpdate, onDelete }) {
  const [editing, setEditing]   = useState(false);
  const [label, setLabel]       = useState(task.label);
  const [interval, setInterval] = useState(task.interval_days);
  const [notes, setNotes]       = useState(task.notes || '');
  const [saving, setSaving]     = useState(false);

  const overdueDays = task.next_due_date ? daysSince(task.next_due_date) : null;
  const isDue       = overdueDays !== null && overdueDays >= 0;
  const isOverdue   = overdueDays !== null && overdueDays > 0;

  async function handleSave() {
    setSaving(true);
    const { data } = await supabase.from('recurring_tasks')
      .update({ label, interval_days: interval, notes: notes || null, updated_at: new Date().toISOString() })
      .eq('id', task.id).select().single();
    setSaving(false); setEditing(false);
    if (data) onUpdate(data);
  }

  async function handleToggleActive() {
    const { data } = await supabase.from('recurring_tasks')
      .update({ active: !task.active }).eq('id', task.id).select().single();
    if (data) onUpdate(data);
  }

  const urgencyColor = isOverdue ? (overdueDays > 3 ? C.red : C.amber) : isDue ? C.green : C.mid;

  return (
    <div style={{
      padding: '0.85rem 1rem', background: C.white, borderRadius: '10px',
      marginBottom: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      opacity: task.active ? 1 : 0.5, borderLeft: `3px solid ${urgencyColor}`,
    }}>
      {!editing ? (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '140px' }}>
            <p style={{ margin: '0 0 0.2rem', fontWeight: 600, fontSize: '0.88rem', color: C.charcoal }}>{task.label}</p>
            <div style={{ display: 'flex', gap: '0.55rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: '0.71rem', color: C.mid, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <RefreshCw size={10} />{INTERVAL_OPTIONS.find(o => o.value === task.interval_days)?.label || `Every ${task.interval_days} days`}
              </span>
              {task.last_done_date && <span style={{ fontSize: '0.71rem', color: C.mid }}>Last done: {formatDate(task.last_done_date)}</span>}
              {task.notes && <span style={{ fontSize: '0.71rem', color: C.mid, fontStyle: 'italic' }}>{task.notes}</span>}
            </div>
          </div>
          <div style={{ flexShrink: 0, textAlign: 'right' }}>
            {task.next_due_date ? (
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: urgencyColor, background: `${urgencyColor}18`, padding: '0.18rem 0.5rem', borderRadius: '999px' }}>
                {isOverdue ? `${overdueDays}d overdue` : isDue ? 'Due today' : `Due ${formatDate(task.next_due_date)}`}
              </span>
            ) : (
              <span style={{ fontSize: '0.72rem', color: C.mid }}>Not yet done</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
            <button onClick={() => setEditing(true)} style={{ padding: '0.28rem 0.65rem', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.white, color: C.mid, fontSize: '0.73rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.28rem' }}>
              <Edit3 size={11} />Edit
            </button>
            <button onClick={handleToggleActive} style={{ padding: '0.28rem 0.65rem', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.white, color: task.active ? C.amber : C.green, fontSize: '0.73rem', cursor: 'pointer' }}>
              {task.active ? 'Pause' : 'Resume'}
            </button>
            <button onClick={() => onDelete(task.id)} style={{ padding: '0.28rem 0.5rem', borderRadius: '6px', border: `1px solid ${C.redLight}`, background: C.redLight, color: C.red, fontSize: '0.73rem', cursor: 'pointer' }}>
              <Trash2 size={12} />
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
          <input value={label} onChange={e => setLabel(e.target.value)}
            style={{ padding: '0.4rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '7px', fontSize: '0.88rem', color: C.charcoal }} />
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.75rem', color: C.mid, fontWeight: 500 }}>Repeat:</span>
            <PillRow options={INTERVAL_OPTIONS} value={interval} onChange={setInterval} color={C.navy} />
          </div>
          <input value={notes} onChange={e => setNotes(e.target.value)} placeholder='Notes (optional)'
            style={{ padding: '0.38rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '7px', fontSize: '0.82rem', color: C.charcoal }} />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={handleSave} disabled={saving} style={{ padding: '0.38rem 1rem', background: C.navy, color: C.white, border: 'none', borderRadius: '7px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => { setEditing(false); setLabel(task.label); setInterval(task.interval_days); setNotes(task.notes||''); }} style={{ padding: '0.38rem 0.85rem', border: `1px solid ${C.border}`, borderRadius: '7px', background: C.white, fontSize: '0.82rem', color: C.mid, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Recurring tasks panel ────────────────────────────────────────────────────
function RecurringTasksPanel({ venue, department }) {
  const [tasks, setTasks]             = useState([]);
  const [loaded, setLoaded]           = useState(false);
  const [addLabel, setAddLabel]       = useState('');
  const [addInterval, setAddInterval] = useState(7);
  const [addNotes, setAddNotes]       = useState('');
  const [adding, setAdding]           = useState(false);
  const [showForm, setShowForm]       = useState(false);

  useEffect(() => {
    setLoaded(false);
    supabase.from('recurring_tasks').select('*')
      .eq('venue', venue).eq('department', department).order('created_at')
      .then(({ data }) => { setTasks(data || []); setLoaded(true); });
  }, [venue, department]);

  async function handleAdd() {
    if (!addLabel.trim()) return;
    setAdding(true);
    const { data } = await supabase.from('recurring_tasks').insert({
      venue, department, label: addLabel.trim(),
      interval_days: addInterval, notes: addNotes.trim() || null, active: true,
    }).select().single();
    if (data) setTasks(prev => [...prev, data]);
    setAddLabel(''); setAddInterval(7); setAddNotes(''); setAdding(false); setShowForm(false);
  }

  async function handleDelete(id) {
    await supabase.from('recurring_tasks').delete().eq('id', id);
    setTasks(prev => prev.filter(t => t.id !== id));
  }

  function handleUpdate(updated) {
    setTasks(prev => prev.map(t => t.id === updated.id ? updated : t));
  }

  const dueTasks = tasks.filter(t => t.active && t.next_due_date && daysSince(t.next_due_date) >= 0);

  return (
    <div style={{ background: C.amberLight, borderRadius: '12px', padding: '1.25rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <RefreshCw size={15} style={{ color: C.amber }} />
            <span style={{ fontWeight: 700, fontSize: '0.95rem', color: C.charcoal }}>Recurring Tasks</span>
            {dueTasks.length > 0 && (
              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: C.amber, background: `${C.amber}22`, padding: '0.12rem 0.45rem', borderRadius: '999px' }}>
                {dueTasks.length} due
              </span>
            )}
          </div>
          <p style={{ margin: '0.12rem 0 0', fontSize: '0.74rem', color: C.mid }}>Tasks done on a schedule — appear as alerts until ticked off</p>
        </div>
        <button onClick={() => setShowForm(p => !p)} style={{
          display: 'flex', alignItems: 'center', gap: '0.35rem',
          padding: '0.38rem 0.9rem', borderRadius: '7px',
          background: showForm ? C.navy : C.white,
          color: showForm ? C.white : C.navy,
          border: `1px solid ${showForm ? C.navy : C.border}`,
          fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
        }}>
          <Plus size={13} />{showForm ? 'Cancel' : 'Add Task'}
        </button>
      </div>

      {showForm && (
        <div style={{ background: C.white, borderRadius: '10px', padding: '1rem', marginBottom: '0.85rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
            <input value={addLabel} onChange={e => setAddLabel(e.target.value)}
              placeholder='Task label, e.g. Deep clean fridge'
              style={{ padding: '0.42rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '7px', fontSize: '0.88rem', color: C.charcoal }} />
            <div>
              <p style={{ margin: '0 0 0.35rem', fontSize: '0.73rem', fontWeight: 600, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Repeat interval</p>
              <PillRow options={INTERVAL_OPTIONS} value={addInterval} onChange={setAddInterval} color={C.amber} />
            </div>
            <input value={addNotes} onChange={e => setAddNotes(e.target.value)}
              placeholder='Notes / instructions (optional)'
              style={{ padding: '0.38rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '7px', fontSize: '0.82rem', color: C.charcoal }} />
            <button onClick={handleAdd} disabled={!addLabel.trim() || adding} style={{
              padding: '0.42rem 1.1rem', background: addLabel.trim() ? C.navy : C.border,
              color: C.white, border: 'none', borderRadius: '7px',
              fontSize: '0.84rem', fontWeight: 600,
              cursor: addLabel.trim() ? 'pointer' : 'not-allowed',
              alignSelf: 'flex-start',
            }}>
              {adding ? 'Adding…' : 'Add Task'}
            </button>
          </div>
        </div>
      )}

      {!loaded && <p style={{ color: C.mid, fontSize: '0.84rem' }}>Loading…</p>}
      {loaded && tasks.length === 0 && <p style={{ color: C.mid, fontSize: '0.84rem', margin: 0 }}>No recurring tasks yet.</p>}
      {tasks.map(t => (
        <RecurringTaskRow key={t.id} task={t} onUpdate={handleUpdate} onDelete={handleDelete} />
      ))}
    </div>
  );
}

// ─── Main admin page ──────────────────────────────────────────────────────────
export default function ChecklistAdmin({ currentUser }) {
  const [venue, setVenue] = useState(ALL_VENUES[0]);
  const [dept, setDept]   = useState('bar');
  const [shift, setShift] = useState('opening');
  const [saved, setSaved] = useState(false);

  const validDepts = useMemo(() => VENUE_DEPTS[venue] || [], [venue]);

  useEffect(() => {
    if (!validDepts.includes(dept)) setDept(validDepts[0] || 'kitchen');
  }, [venue, dept, validDepts]);

  // Bar: all shifts including cleaning
  // Kitchen: cleaning only (add opening/closing here if needed in future)
  const validShifts = useMemo(() => {
    return dept === 'kitchen' ? KITCHEN_SHIFTS : BAR_SHIFTS;
  }, [dept]);

  useEffect(() => {
    if (!validShifts.includes(shift)) setShift(validShifts[0]);
  }, [dept, shift, validShifts]);

  function handleSaved() {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div style={{ padding: '1.25rem', maxWidth: '900px', margin: '0 auto', background: C.cream, minHeight: '100vh' }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <h2 style={{ margin: '0 0 0.2rem', fontSize: '1.2rem', fontWeight: 700, color: C.charcoal }}>Checklist Templates</h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: C.mid }}>Manage checklist items and recurring tasks per venue and department</p>
        </div>
        {saved && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.38rem 0.85rem', background: C.greenLight, border: `1px solid ${C.green}44`, borderRadius: '8px', fontSize: '0.8rem', color: C.green, fontWeight: 600 }}>
            <CheckCircle size={13} />Saved
          </div>
        )}
      </div>

      {/* Venue */}
      <div style={{ marginBottom: '1.25rem' }}>
        <p style={{ margin: '0 0 0.5rem', fontSize: '0.7rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Venue</p>
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          {ALL_VENUES.map(v => (
            <button key={v} onClick={() => setVenue(v)} style={{
              padding: '0.38rem 0.9rem', borderRadius: '999px',
              border: `1px solid ${venue === v ? C.navy : C.border}`,
              background: venue === v ? C.navy : C.white,
              color: venue === v ? C.white : C.mid,
              fontSize: '0.83rem', cursor: 'pointer', fontWeight: venue === v ? 600 : 400,
            }}>{v}</button>
          ))}
        </div>
      </div>

      {/* Department */}
      <div style={{ marginBottom: '1.25rem' }}>
        <p style={{ margin: '0 0 0.5rem', fontSize: '0.7rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Department</p>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          {validDepts.map(d => (
            <button key={d} onClick={() => setDept(d)} style={{
              padding: '0.38rem 1rem', borderRadius: '999px',
              border: `2px solid ${dept === d ? C.pinkActive : C.border}`,
              background: dept === d ? C.pinkActive : C.white,
              color: dept === d ? C.white : C.mid,
              fontSize: '0.84rem', cursor: 'pointer', fontWeight: dept === d ? 700 : 400,
            }}>{DEPT_LABELS[d]}</button>
          ))}
        </div>
      </div>

      {/* Shift tabs */}
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {validShifts.map(s => {
          const meta   = SHIFT_META[s];
          const Icon   = meta.icon;
          const active = shift === s;
          return (
            <button key={s} onClick={() => setShift(s)} style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.42rem 1rem', borderRadius: '999px',
              border: `2px solid ${active ? meta.colors.active : C.border}`,
              background: active ? meta.colors.active : C.white,
              color: active ? C.white : C.mid,
              fontSize: '0.83rem', cursor: 'pointer', fontWeight: active ? 700 : 400,
            }}>
              <Icon size={13} />{meta.label}
            </button>
          );
        })}
      </div>

      {/* Template editor */}
      <div style={{ marginBottom: '1.5rem' }}>
        <ShiftTemplateEditor
          key={`${venue}-${dept}-${shift}`}
          venue={venue}
          department={dept}
          shift={shift}
          onSaved={handleSaved}
        />
      </div>

      {/* Recurring tasks */}
      <RecurringTasksPanel
        key={`recurring-${venue}-${dept}`}
        venue={venue}
        department={dept}
      />

      <div style={{ marginTop: '1.5rem', padding: '0.85rem 1rem', background: C.skyBlueLight, border: `1px solid ${C.skyBlue}`, borderRadius: '10px' }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
          <AlertTriangle size={14} style={{ color: C.skyText, flexShrink: 0, marginTop: '0.1rem' }} />
          <div style={{ fontSize: '0.78rem', color: C.skyText, lineHeight: 1.55 }}>
            <strong>How this works:</strong> Recurring tasks appear as alerts at the top of the checklist page for the relevant venue and department. They carry forward every day until ticked off by staff. The interval timer resets only when the task is marked complete.
          </div>
        </div>
      </div>
    </div>
  );
}